import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Group, Modal, Stack, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';

import { createRole, deleteRole, listRoles, updateRole } from '../../api/resources';
import { DetailDrawer } from '../../components/detail/DetailDrawer';
import { RoleDetailPanel } from '../../components/detail/RoleDetailPanel';
import { EntityRef } from '../../components/EntityRef';
import { PageHeader } from '../../components/PageHeader';
import { PaginatedTable } from '../../components/PaginatedTable';
import { RemoveEntityAction } from '../../components/RemoveEntityAction';
import { roleLabel } from '../../lib/labels';
import { DEFAULT_PAGE_SIZE, useListQuery } from '../../hooks/useListQuery';
import type { Role } from '../../types/api';

export function RolesPage() {
    const { t } = useTranslation();
    const [page, setPage] = useState(1);
    const [detail, setDetail] = useState<Role | null>(null);
    const [createOpen, setCreateOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const queryClient = useQueryClient();
    const { data, isLoading } = useListQuery('roles', listRoles, { page, scope: 'platform' });

    const createForm = useForm({
        initialValues: { name: '', description: '', scope: 'platform' },
    });
    const editForm = useForm({
        initialValues: { name: '', description: '' },
    });

    const invalidateRoles = () => void queryClient.invalidateQueries({ queryKey: ['roles'] });

    const createMutation = useMutation({
        mutationFn: () => createRole(createForm.values),
        onSuccess: () => {
            notifications.show({ color: 'green', message: t('features.roles.created') });
            setCreateOpen(false);
            createForm.reset();
            invalidateRoles();
        },
        onError: (e: Error) => notifications.show({ color: 'red', message: e.message }),
    });

    const updateMutation = useMutation({
        mutationFn: (roleId: string) =>
            updateRole(roleId, {
                name: editForm.values.name.trim(),
                description: editForm.values.description.trim(),
            }),
        onSuccess: (updated) => {
            notifications.show({ color: 'green', message: t('features.roles.updated') });
            setEditOpen(false);
            setDetail(updated);
            invalidateRoles();
        },
        onError: (e: Error) => notifications.show({ color: 'red', message: e.message }),
    });

    const openEdit = () => {
        if (!detail) {
            return;
        }
        editForm.setValues({
            name: detail.name,
            description: detail.description ?? '',
        });
        setEditOpen(true);
    };

    return (
        <Stack gap="lg">
            <PageHeader
                title={t('features.roles.title')}
                description={t('features.roles.description')}
                actions={
                    <Button onClick={() => setCreateOpen(true)}>
                        {t('features.roles.createAction')}
                    </Button>
                }
            />
            <PaginatedTable
                rowKey={(r) => r.id}
                columns={[
                    {
                        key: 'name',
                        header: t('fields.role'),
                        render: (r) => (
                            <EntityRef
                                title={roleLabel(r)}
                                id={r.id}
                                secondary={r.description}
                                hideId={false}
                            />
                        ),
                    },
                ]}
                rows={data?.data ?? []}
                total={data?.total ?? 0}
                page={page}
                pageSize={DEFAULT_PAGE_SIZE}
                onPageChange={setPage}
                isLoading={isLoading}
                onRowClick={setDetail}
            />
            <DetailDrawer
                opened={Boolean(detail)}
                onClose={() => setDetail(null)}
                title={t('detail.role')}
                footer={
                    detail ? (
                        <Group justify="space-between" w="100%">
                            <Button variant="light" onClick={openEdit}>
                                {t('common.actions.edit')}
                            </Button>
                            <RemoveEntityAction
                                entityId={detail.id}
                                entityName={roleLabel(detail)}
                                deleteFn={deleteRole}
                                queryKeys={['roles', 'roles-platform']}
                                successMessageKey="features.roles.removed"
                                onRemoved={() => setDetail(null)}
                            />
                        </Group>
                    ) : undefined
                }
            >
                {detail ? <RoleDetailPanel role={detail} /> : null}
            </DetailDrawer>
            <Modal
                opened={createOpen}
                onClose={() => setCreateOpen(false)}
                title={t('features.roles.createModalTitle')}
            >
                <Stack>
                    <TextInput label={t('fields.name')} required {...createForm.getInputProps('name')} />
                    <TextInput label={t('fields.description')} {...createForm.getInputProps('description')} />
                    <Button loading={createMutation.isPending} onClick={() => createMutation.mutate()}>
                        {t('common.actions.create')}
                    </Button>
                </Stack>
            </Modal>
            <Modal
                opened={editOpen}
                onClose={() => setEditOpen(false)}
                title={t('features.roles.editModalTitle')}
            >
                <Stack>
                    <TextInput label={t('fields.name')} required {...editForm.getInputProps('name')} />
                    <TextInput label={t('fields.description')} {...editForm.getInputProps('description')} />
                    <Group justify="flex-end">
                        <Button variant="default" onClick={() => setEditOpen(false)}>
                            {t('common.actions.cancel')}
                        </Button>
                        <Button
                            loading={updateMutation.isPending}
                            onClick={() => detail && updateMutation.mutate(detail.id)}
                        >
                            {t('common.actions.save')}
                        </Button>
                    </Group>
                </Stack>
            </Modal>
        </Stack>
    );
}
